/**
 * CadastroDeUsuarioModal — popup "Cadastrar usuário" da Matriz de Acessos.
 *
 * POST /access-matrix/users  → useCreateUser()  [Admin]
 *
 * O cadastro registra a pessoa (nome, e-mail, cargo) já com o perfil de acesso. Ela entra
 * depois por "Entrar com Microsoft" com o mesmo e-mail e recebe o perfil escolhido aqui —
 * sem cadastro, o primeiro login cai no perfil padrão. Não há senha neste formulário.
 *
 * a11y: role="dialog", aria-modal, foco no primeiro campo ao abrir, Tab preso no diálogo,
 * Esc fecha e o foco VOLTA ao botão que abriu; erro de campo ligado por aria-describedby.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { UserPlus, X } from 'lucide-react';
import { C } from '@/theme/palette';
import { Btn } from '@/components/ui';
import { MutationError } from '@/components/ui/QueryState';

const FOCAVEIS = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const VAZIO = { nome: '', email: '', cargo: '', perfil: '' };

/** Regras do formulário — as mesmas da API (`CreateUserIn`). Pura, para testar. */
export function validarCadastro({ nome, email, cargo, perfil }) {
  const erros = {};
  const n = (nome ?? '').trim();
  const e = (email ?? '').trim();
  if (!n) erros.nome = 'Informe o nome.';
  else if (n.length > 120) erros.nome = 'O nome tem no máximo 120 caracteres.';
  if (!e) erros.email = 'Informe o e-mail.';
  else if (e.length > 254 || !EMAIL_RE.test(e)) erros.email = 'E-mail inválido.';
  if ((cargo ?? '').trim().length > 80) erros.cargo = 'O cargo tem no máximo 80 caracteres.';
  if (!perfil) erros.perfil = 'Escolha o perfil de acesso.';
  return erros;
}

const rotulo = { display: 'block', fontSize: 12.5, fontWeight: 600, color: C.neutral, marginBottom: 6 };
const campo = (comErro) => ({
  width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '8px 12px', fontSize: 14,
  background: C.black, color: C.neutral, borderRadius: 8, outlineOffset: 2,
  border: `1px solid ${comErro ? C.hot : C.blackBorder}`,
});

function Campo({ id, label, erro, dica, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={id} style={rotulo}>{label}</label>
      {children}
      {dica && !erro && (
        <p id={`${id}-dica`} style={{ margin: '5px 0 0', fontSize: 12, color: C.neutralDim }}>{dica}</p>
      )}
      {erro && (
        <p id={`${id}-erro`} role="alert" style={{ margin: '5px 0 0', fontSize: 12, color: C.hot }}>{erro}</p>
      )}
    </div>
  );
}

export function CadastroDeUsuarioModal({ open, matrices, salvando = false, erro = null, onSalvar, onFechar }) {
  const uid = useId();
  const ids = { nome: `${uid}-nome`, email: `${uid}-email`, cargo: `${uid}-cargo`, perfil: `${uid}-perfil` };
  const [valores, setValores] = useState(VAZIO);
  const [erros, setErros] = useState({});
  const painelRef = useRef(null);
  const primeiroRef = useRef(null);
  const retornoRef = useRef(null);

  const perfis = (matrices ?? []).filter((m) => m.is_active !== false);

  // Abre limpo, com o foco no primeiro campo, e devolve o foco a quem abriu ao fechar.
  useEffect(() => {
    if (!open) return undefined;
    retornoRef.current = document.activeElement;
    setValores(VAZIO);
    setErros({});
    primeiroRef.current?.focus();
    return () => {
      const el = retornoRef.current;
      if (el && typeof el.focus === 'function' && document.contains(el)) el.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !salvando) { onFechar?.(); return; }
      if (e.key === 'Tab' && painelRef.current) {
        const itens = [...painelRef.current.querySelectorAll(FOCAVEIS)];
        if (!itens.length) return;
        const primeiro = itens[0];
        const ultimo = itens[itens.length - 1];
        if (e.shiftKey && document.activeElement === primeiro) { e.preventDefault(); ultimo.focus(); }
        else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primeiro.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, salvando, onFechar]);

  if (!open) return null;

  const mudar = (chave) => (e) => {
    const v = e.target.value;
    setValores((ant) => ({ ...ant, [chave]: v }));
    if (erros[chave]) setErros((ant) => ({ ...ant, [chave]: undefined }));
  };

  const enviar = (e) => {
    e.preventDefault();
    if (salvando) return;
    const encontrados = validarCadastro(valores);
    setErros(encontrados);
    const primeiroComErro = ['nome', 'email', 'cargo', 'perfil'].find((k) => encontrados[k]);
    if (primeiroComErro) {
      document.getElementById(ids[primeiroComErro])?.focus();
      return;
    }
    onSalvar?.({
      displayName: valores.nome.trim(),
      email: valores.email.trim().toLowerCase(),
      roleLabel: valores.cargo.trim(),
      matrixId: valores.perfil,
    });
  };

  const descrito = (k, temDica) => (erros[k] ? `${ids[k]}-erro` : temDica ? `${ids[k]}-dica` : undefined);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${uid}-titulo`}
      onClick={(e) => { if (e.target === e.currentTarget && !salvando) onFechar?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
        background: `${C.bgDeep}C4`, backdropFilter: 'blur(5px)',
      }}
    >
      <form
        ref={painelRef}
        onSubmit={enviar}
        noValidate
        style={{
          width: '100%', maxWidth: 520, background: C.blackElev,
          backgroundImage: `linear-gradient(135deg, ${C.blackElev2}, ${C.blackElev})`,
          border: `1px solid ${C.blackBorder}`, borderRadius: 16,
          padding: 24, boxShadow: `0 25px 80px ${C.bgDeep}CC`, maxHeight: 'calc(100dvh - 32px)',
          overflow: 'auto',
        }}
      >
        <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
          <UserPlus size={18} style={{ color: C.cyber }} aria-hidden="true" />
          <h3 id={`${uid}-titulo`} style={{ margin: 0, fontSize: 17, fontWeight: 650, color: C.neutral, flex: 1 }}>
            Cadastrar usuário
          </h3>
          <button
            type="button"
            onClick={() => !salvando && onFechar?.()}
            aria-label="Fechar" title="Fechar"
            style={{
              background: 'none', border: `1px solid ${C.blackBorder}`, color: C.neutralDim,
              cursor: 'pointer', width: 32, height: 32, borderRadius: 8, display: 'grid', placeItems: 'center',
            }}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: C.neutralDim, lineHeight: 1.55 }}>
          A pessoa entra com <strong style={{ color: C.neutral }}>Entrar com Microsoft</strong> usando
          este e-mail e já recebe o perfil escolhido. Não há senha para definir.
        </p>

        <Campo id={ids.nome} label="Nome completo" erro={erros.nome}>
          <input
            ref={primeiroRef} id={ids.nome} type="text" autoComplete="off" maxLength={120}
            value={valores.nome} onChange={mudar('nome')} disabled={salvando}
            aria-invalid={!!erros.nome} aria-describedby={descrito('nome')} style={campo(erros.nome)}
          />
        </Campo>

        <Campo id={ids.email} label="E-mail corporativo" erro={erros.email}
          dica="O mesmo e-mail da conta Microsoft da pessoa.">
          <input
            id={ids.email} type="email" autoComplete="off" inputMode="email" maxLength={254}
            value={valores.email} onChange={mudar('email')} disabled={salvando}
            aria-invalid={!!erros.email} aria-describedby={descrito('email', true)} style={campo(erros.email)}
          />
        </Campo>

        <Campo id={ids.cargo} label="Cargo (opcional)" erro={erros.cargo}>
          <input
            id={ids.cargo} type="text" autoComplete="off" maxLength={80}
            value={valores.cargo} onChange={mudar('cargo')} disabled={salvando}
            aria-invalid={!!erros.cargo} aria-describedby={descrito('cargo')} style={campo(erros.cargo)}
          />
        </Campo>

        <Campo id={ids.perfil} label="Perfil de acesso" erro={erros.perfil}
          dica="Define o que a pessoa vê e faz. Pode ser trocado depois na tabela de usuários.">
          <select
            id={ids.perfil} value={valores.perfil} onChange={mudar('perfil')} disabled={salvando}
            aria-invalid={!!erros.perfil} aria-describedby={descrito('perfil', true)} style={campo(erros.perfil)}
          >
            <option value="">Selecione…</option>
            {perfis.map((m) => (
              <option key={m.id} value={m.id}>{m.display_name ?? m.name ?? m.slug}</option>
            ))}
          </select>
        </Campo>

        {erro && <MutationError error={erro} />}

        <div className="flex items-center justify-end gap-2" style={{ flexWrap: 'wrap', marginTop: 6 }}>
          <Btn variant="ghost" size="md" onClick={() => !salvando && onFechar?.()}>
            Cancelar
          </Btn>
          <button
            type="submit"
            disabled={salvando}
            style={{
              background: C.cyber, color: C.black, border: `1px solid ${C.cyber}`, borderRadius: 8,
              minHeight: 40, padding: '0 18px', fontSize: 14, fontWeight: 650,
              cursor: salvando ? 'wait' : 'pointer', opacity: salvando ? 0.7 : 1,
            }}
          >
            {salvando ? 'Cadastrando…' : 'Cadastrar'}
          </button>
        </div>
      </form>
    </div>
  );
}
